import { useRef, useState } from "react";
import { Brush, Check, ImagePlus, Images, MonitorUp, Trash2, Upload, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { EPISODES } from "@/data/magicalContent";

const API = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (path) => `${API}/api/media/files/${path}`;

export function AdminMediaPanel({ library, fanArt, posters, onChanged, onError }) {
  const { approveFanArt, deleteMediaFile, setEpisodePoster, clearEpisodePoster } = useAuth();
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [assignChoices, setAssignChoices] = useState({});
  const fileRef = useRef(null);

  const assignedEpisode = (storagePath) => {
    const entry = Object.entries(posters || {}).find(([, path]) => path === storagePath);
    return entry ? Number(entry[0]) : null;
  };

  const assignPoster = async (item) => {
    const episode = assignChoices[item.id] || 1;
    try {
      await setEpisodePoster(episode, item.storage_path);
      onChanged();
    } catch {
      onError("Could not set that poster.");
    }
  };

  const clearPoster = async (episode) => {
    try {
      await clearEpisodePoster(episode);
      onChanged();
    } catch {
      onError("Could not clear that poster.");
    }
  };

  const upload = async (event) => {
    event.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("title", title);
      const response = await fetch(`${API}/api/media/admin/upload`, { method: "POST", credentials: "include", body });
      if (!response.ok) throw new Error("upload failed");
      setTitle("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onChanged();
    } catch {
      onError("Upload failed. Use a JPG, PNG, WEBP or GIF under 8 MB.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteMediaFile(id);
      onChanged();
    } catch {
      onError("Could not remove that file.");
    }
  };

  const toggleApprove = async (id) => {
    try {
      await approveFanArt(id);
      onChanged();
    } catch {
      onError("Could not update that artwork.");
    }
  };

  return (
    <section className="ns-admin-two-column">
      <article className="ns-activity-panel" data-testid="admin-media-library">
        <p className="ns-kicker"><Images size={14} /> Media library</p>
        <form className="ns-media-upload" onSubmit={upload}>
          <input type="text" data-testid="media-title-input" placeholder="Title (e.g. Episode 6 poster)" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120} />
          <label className="ns-fanart-file">
            <ImagePlus size={15} /> {file ? file.name : "Choose image"}
            <input ref={fileRef} type="file" data-testid="media-file-input" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(event) => setFile(event.target.files?.[0] || null)} hidden />
          </label>
          <button className="ns-button ns-button-primary" data-testid="media-upload-button" disabled={busy || !file}>{busy ? "Uploading…" : <><Upload size={15} /> Upload</>}</button>
        </form>
        <div className="ns-media-grid">
          {library.map((item) => {
            const posterEpisode = assignedEpisode(item.storage_path);
            return (
              <figure key={item.id} data-testid={`media-item-${item.id}`}>
                <img src={fileUrl(item.storage_path)} alt={item.title || "Library image"} loading="lazy" />
                <figcaption><span>{item.title || "Untitled"}</span><button data-testid={`media-delete-${item.id}`} aria-label="Delete image" onClick={() => remove(item.id)}><Trash2 size={14} /></button></figcaption>
                {posterEpisode ? (
                  <span className="ns-poster-chip" data-testid={`media-poster-chip-${item.id}`}>Poster · Ep {posterEpisode} <button data-testid={`media-poster-clear-${item.id}`} aria-label="Clear poster" onClick={() => clearPoster(posterEpisode)}><X size={12} /></button></span>
                ) : (
                  <div className="ns-poster-assign">
                    <select data-testid={`media-poster-select-${item.id}`} value={assignChoices[item.id] || 1} onChange={(event) => setAssignChoices((current) => ({ ...current, [item.id]: Number(event.target.value) }))}>
                      {EPISODES.map((episode) => <option key={episode.number} value={episode.number}>Episode {episode.number}</option>)}
                    </select>
                    <button data-testid={`media-poster-set-${item.id}`} onClick={() => assignPoster(item)}><MonitorUp size={13} /> Set poster</button>
                  </div>
                )}
              </figure>
            );
          })}
        </div>
        {!library.length && <p>No uploads yet. Posters and artwork you add here get a permanent link.</p>}
      </article>
      <article className="ns-activity-panel" data-testid="admin-fanart-panel">
        <p className="ns-kicker"><Brush size={14} /> Fan art review</p>
        <div>
          {fanArt.map((item) => (
            <article key={item.id} data-testid={`fanart-review-${item.id}`}>
              <img className="ns-fanart-thumb" src={fileUrl(item.storage_path)} alt={`Fan art by ${item.name || "little artist"}`} loading="lazy" />
              <div><strong>{item.name || "Little artist"}</strong><span>{item.message || "No message"}</span></div>
              <div className="ns-fanart-actions">
                <button className={item.approved ? "is-active" : ""} data-testid={`fanart-approve-${item.id}`} onClick={() => toggleApprove(item.id)}><Check size={14} /> {item.approved ? "Featured" : "Feature"}</button>
                <button data-testid={`fanart-delete-${item.id}`} aria-label="Delete artwork" onClick={() => remove(item.id)}><Trash2 size={14} /></button>
              </div>
            </article>
          ))}
        </div>
        {!fanArt.length && <p>No fan art yet. Share the Little Artists' Corner with your families.</p>}
      </article>
    </section>
  );
}
